import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `admin.capabilities` in Brazilian Portuguese.
 *
 * ── Three places where English hides a decision Portuguese has to make ──
 *
 * **`listings.audience`** labels a row that is an *agente* today and could be a *base de
 * conhecimento* tomorrow, and "shared with groups" agrees with neither until it is
 * translated. Rather than pick a gender the map cannot guarantee, the Portuguese is written
 * without a participle at all — *com grupos específicos* — which is idiomatic, shorter, and
 * cannot be wrong about a noun it has never been told (PRD-27 D-21).
 *
 * **`needsOwner.notMoved`** was "It was not moved" — *movido* for an agent, *movida* for a
 * base, and the row renders both kinds. It becomes *Nada foi movido*: the subject is now
 * "nothing", which is masculine in every case and says the same thing.
 *
 * **`needsOwner.since`** wraps `formatAgo`, and Portuguese `RelativeTimeFormat` already
 * carries its preposition — *há 5 min.* — so the English "since" must NOT be translated as
 * *desde*, which would render *desde há 5 min.* (LOC-022). *sem responsável há 5 min.* is the
 * idiomatic Brazilian form and carries the same fact: this is how long it has had nobody.
 * *responsável* is the word for an owner across this catalogue — it serves both genders, which
 * *dono/dona* does not.
 */
export const capabilities: Messages['admin']['capabilities'] = {
  title: 'Redação das capacidades',
  lead: 'As capacidades conectadas ficam escondidas de quem cria até um administrador escrever, em linguagem simples, o que elas fazem. Esta é essa fila.',
  loading: 'Carregando a fila…',
  unavailable: 'A fila de curadoria ainda não está disponível nesta instalação',
  unavailableBody: 'GET /api/admin/mcp/curation-queue não respondeu. A redação já curada não é afetada.',
  empty: 'Nada esperando',
  emptyBody: 'Toda capacidade conectada nesta instalação já tem redação curada.',

  claimedTitle: 'O que o sistema conectado afirma',
  claimedHint: 'Escrito por esse sistema, não por alguém daqui. Confira contra o que a capacidade realmente faz.',
  useAsDraft: 'Usar como ponto de partida',
  riskLabel: 'risco',
  curatedBadge: 'curada',
  uncuratedBadge: 'escondida de quem cria',

  nameLabel: 'Nome que quem cria e as pessoas vão ver',
  namePlaceholder: 'Abrir um chamado de suporte',
  descriptionLabel: 'O que ela faz, em uma ou duas frases',
  descriptionPlaceholder: 'Abre um chamado no sistema de suporte em nome da pessoa. Não consegue fechar nem editar um.',
  wordingLabel: 'Ela lê ou ela age?',
  wordingReads: 'Lê informações',
  wordingActs: 'Executa uma ação — a pessoa é sempre consultada antes',
  wordingHint:
    'Isso decide qual aviso a pessoa vê antes da primeira conversa. Tudo que muda alguma coisa fora desta plataforma age.',

  save: 'Salvar redação',
  saving: 'Salvando…',
  saved: 'Salvo — quem cria já consegue ver esta capacidade.',
  saveFailed: 'Não foi possível salvar a redação.',
  withdraw: 'Retirar a redação',
  withdrawing: 'Retirando…',
  withdrawHint: 'Retirar esconde de novo de quem cria. Os agentes que já usam não são afetados.',
  withdrawFailed: 'Não foi possível retirar a redação.',

  nameCount: (fmt, left) =>
    pluralCategory(fmt.locale, left) === 'one' ? `resta ${fmt.formatCount(left)}` : `restam ${fmt.formatCount(left)}`,
  descriptionCount: (fmt, left) =>
    pluralCategory(fmt.locale, left) === 'one' ? `resta ${fmt.formatCount(left)}` : `restam ${fmt.formatCount(left)}`,

  listings: {
    audience: {
      private: 'só quem mantém',
      groups: 'com grupos específicos',
      everyone: 'todo mundo'
    },

    state: {
      draft: 'Rascunho',
      published: 'Publicado',
      archived: 'Descontinuado',
      suspended: 'Pausado por um administrador'
    },

    needsOwner: {
      hint: 'Mover algo nunca muda quem pode usar. O selo Oficial cai junto com a mudança, até um administrador concedê-lo de novo.',
      loading: 'Carregando…',
      failed: 'Não foi possível carregar esta lista. Nada foi alterado.',
      empty: 'Tudo tem alguém que pode manter.',
      kind: { agent: 'Agente', knowledge_base: 'Base de conhecimento' },
      ownedBy: 'Pertence a',
      ownerLeft: (name) => `Pertence a ${name}, cuja conta está desativada`,
      groupUnmanaged: 'que não tem gestores',
      since: (when) => `sem responsável ${when}`,
      choose: 'Escolher um novo responsável',
      chooseLabel: (name) => `Escolher um novo responsável para ${name}`,
      move: 'Mover',
      moving: 'Movendo…',
      cancel: 'Cancelar',
      notMoved: 'Nada foi movido.',
      moved: (name, owner) => `${name} agora pertence a ${owner}.`
    }
  }
}
